import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MODULES, modulesForPlan, PLAN_LABELS, PLANS, TENANT_STATUS_LABELS, TENANT_STATUSES, TRIAL_DAYS, TRIAL_LIMITS, TRIAL_PLAN, type ModuleKey, type Plan, type TenantStatus, type TenantSummary, INCLUDED_USERS } from '@kontor/shared';
import { ApiError } from '../api';
import { FilterBar, type FilterField } from '../components/filter/FilterBar';
import { useMeta } from '../meta';
import { operatorApi } from './operatorApi';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const day = (d: string) => dateFormat.format(new Date(`${d}T12:00:00`));
/** Kleinschreibung ohne Akzente für die Suche */
const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').replace(/ß/g, 'ss').toLowerCase();

export const statusTone = (s: TenantStatus) => (s === 'active' ? 'badge--won' : s === 'trial' || s === 'suspended' ? 'badge--in_progress' : 'badge--rejected');

/** Zusatzmodule, die das Paket nicht schon enthält und die es schon gibt oder als Zusatz buchbar sind */
export const addonChoices = (plan: Plan) => MODULES.filter((m) => !(modulesForPlan(plan) as string[]).includes(m.key));

/** Liste aller Mandanten mit Suche und Filter nach Status, dazu das Formular für einen neuen Mandanten (KON-092, KON-095). */
export function OperatorTenantsPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const [creating, setCreating] = useState(false);

  const list = useQuery({ queryKey: ['operator-tenants'], queryFn: async () => (await operatorApi<{ data: TenantSummary[] }>('GET', '/tenants')).data });
  const words = fold(q).split(/\s+/).filter(Boolean);
  const shown = (list.data ?? []).filter((t) => (!status || t.status === status) && words.every((w) => fold(`${t.name} ${t.number} ${t.slug}`).includes(w)));

  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    setParams(merged);
  };
  const fields: FilterField[] = [{ key: 'status', label: 'Status', type: 'select', options: [{ value: '', label: 'Alle' }, ...TENANT_STATUSES.map((s) => ({ value: s, label: TENANT_STATUS_LABELS[s] }))] }];

  return (
    <>
      <div className="page-head">
        <h1>Mandanten</h1>
        <span className="muted">{list.data ? `${shown.length} von ${list.data.length}` : ''}</span>
        {!creating && <button type="button" className="btn btn--primary btn--sm page-head__action" onClick={() => setCreating(true)}>Neuer Mandant</button>}
      </div>

      {creating && <NewTenantForm onClose={() => setCreating(false)} />}

      <FilterBar
        search={{ value: q, placeholder: 'Name, Mandantennummer oder Kennung', onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        values={{ status }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams())}
      />

      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <th>Mandant</th>
              <th>Paket</th>
              <th>Status</th>
              <th>Benutzer</th>
              <th>Anfragen</th>
              <th>Gültig bis</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={6} className="muted">Wird geladen …</td></tr>}
            {list.data && shown.length === 0 && <tr><td colSpan={6} className="empty">Kein Mandant passt zur Suche.</td></tr>}
            {shown.map((t) => (
              <tr key={t.id}>
                <td>
                  <Link to={`/betreiber/mandanten/${t.id}`} className="strong-link">{t.name}</Link>
                  <div className="muted small">Nr. {t.number}{t.legacyLogin ? ` · auch „${t.slug}“` : ''}</div>
                </td>
                <td data-label="Paket">
                  {PLAN_LABELS[t.plan]}
                  {t.addons.length > 0 && <div className="muted small">+ {t.addons.length} Zusatz</div>}
                </td>
                <td data-label="Status"><span className={`badge ${statusTone(t.status)}`}>{TENANT_STATUS_LABELS[t.status]}</span></td>
                <td data-label="Benutzer">{t.seatsUsed} von {t.maxUsers}</td>
                <td data-label="Anfragen">{t.inquiryCount}</td>
                <td data-label="Gültig bis">{t.validUntil ? day(t.validUntil) : <span className="muted">unbefristet</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function NewTenantForm({ onClose }: { onClose: () => void }) {
  useMeta();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [plan, setPlan] = useState<Plan>('basic');
  const [addons, setAddons] = useState<ModuleKey[]>([]);
  const [maxUsers, setMaxUsers] = useState(INCLUDED_USERS);
  const [validUntil, setValidUntil] = useState('');
  const [trial, setTrial] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => operatorApi<{ data: TenantSummary }>('POST', '/tenants', { name: name.trim(), plan, addons, maxUsers, trial, ...(validUntil ? { validUntil } : {}) }),
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['operator-tenants'] });
      navigate(`/betreiber/mandanten/${res.data.id}`);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setFormError('Bitte prüfen Sie Ihre Eingaben.');
      } else {
        setErrors({});
        setFormError(e instanceof ApiError ? e.message : 'Der Mandant konnte nicht angelegt werden.');
      }
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    create.mutate();
  }
  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  const choices = addonChoices(plan);

  return (
    <form className="card" onSubmit={submit} noValidate>
      <h2>Neuer Mandant</h2>
      <p className="muted small">Die Mandantennummer vergibt Wirkia beim Anlegen. Mit ihr, der E-Mail-Adresse und dem Passwort melden sich die Benutzer an.</p>
      {formError && <div className="alert alert--error" role="alert">{formError}</div>}
      <div className="form-grid">
        <label className="field">
          <span>Name <abbr title="Pflichtfeld">*</abbr></span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" aria-invalid={!!errors.name} autoFocus />
          {error('name')}
        </label>
        <label className="field">
          <span>Paket</span>
          <select className="input" value={plan} onChange={(e) => { setPlan(e.target.value as Plan); setAddons([]); }}>
            {PLANS.map((p) => <option key={p} value={p}>{PLAN_LABELS[p]}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Benutzerplätze</span>
          <input className="input" type="number" min={1} max={1000} value={maxUsers} onChange={(e) => setMaxUsers(Number(e.target.value))} aria-invalid={!!errors.maxUsers} />
          {error('maxUsers')}
          <small className="muted">Ein Mandant beginnt mit {INCLUDED_USERS} Platz, Test oder nicht. Weitere Plätze bucht er selbst (5 € brutto im Monat je Platz) oder Sie geben sie hier frei.</small>
        </label>
        <label className="field">
          <span>Gültig bis</span>
          <input className="input" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} aria-invalid={!!errors.validUntil} />
          {error('validUntil')}
          <small className="muted">Leer lassen für unbefristet, bei einem Test für {TRIAL_DAYS} Tage ab heute.</small>
        </label>
        <label className="check">
          <input type="checkbox" checked={trial} onChange={(e) => { setTrial(e.target.checked); setMaxUsers(e.target.checked ? TRIAL_LIMITS.users : INCLUDED_USERS); if (e.target.checked) setPlan(TRIAL_PLAN); }} />
          <span>Testmandant ({TRIAL_DAYS} Tage, ohne Zahlung)</span>
        </label>
      </div>
      <AddonPicker choices={choices} value={addons} onChange={setAddons} />
      <div className="form-actions">
        <button className="btn btn--primary" type="submit" disabled={create.isPending || name.trim().length < 2}>{create.isPending ? 'Wird angelegt …' : 'Mandant anlegen'}</button>
        <button className="btn btn--secondary" type="button" onClick={onClose}>Abbrechen</button>
      </div>
    </form>
  );
}

/** Zusatzmodule eines Mandanten, die das Paket nicht enthält */
export function AddonPicker({ choices, value, onChange }: { choices: typeof MODULES[number][]; value: ModuleKey[]; onChange: (v: ModuleKey[]) => void }) {
  if (choices.length === 0) return null;
  return (
    <fieldset className="field filter-multi">
      <legend>Zusatzmodule</legend>
      <div className="addons">
        {choices.map((m) => (
          <label key={m.key} className="check">
            <input type="checkbox" checked={value.includes(m.key)} onChange={(e) => onChange(e.target.checked ? [...value, m.key] : value.filter((x) => x !== m.key))} />
            <span>{m.label}{!m.available && <span className="muted small"> (noch nicht gebaut)</span>}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
